import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { removeFromWishlist, useWishlist } from "@/lib/wishlist/store";
import { getCatalogRepository } from "@/lib/catalog/repository";
import type { CatalogProduct } from "@/lib/catalog/products";
import { formatPrice } from "@/lib/cart/pricing";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";
import { startingPrice } from "@/lib/catalog/size-pricing";
import {
  AccountShell,
  SectionCard,
  EmptyState,
  GoldButton,
} from "@/components/account/AccountShell";

export const Route = createFileRoute("/account/wishlist")({
  component: WishlistPage,
});

function WishlistPage() {
  const ids = useWishlist();
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);

  useEffect(() => {
    let alive = true;
    // Always resolve wishlist items through the active CatalogRepository so
    // names, prices and cover_image stay database-sourced (never the RC1
    // mock array), matching Home/Shop/Search/PDP behaviour.
    getCatalogRepository()
      .getProducts()
      .then((products) => {
        if (alive) setCatalog(products);
      })
      .catch(() => {
        if (alive) setCatalog([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const items = ids
    .map((id) => catalog.find((p) => p.id === id))
    .filter((p): p is CatalogProduct => !!p);

  return (
    <AccountShell>
      <SectionCard title="Wishlist">
        {items.length === 0 ? (
          <EmptyState
            title="Your wishlist is empty"
            description="Save fragrances you love — they'll appear here for later."
            action={
              <Link to="/fragrances" search={{}}>
                <GoldButton>Browse fragrances</GoldButton>
              </Link>
            }
          />
        ) : (
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <li key={p.id} className="group">
                <Link
                  to="/fragrances/$slug"
                  params={{ slug: p.slug }}
                  className="block overflow-hidden bg-black/[0.03]"
                >
                  <SafeImage
                    src={p.img}
                    fallback={fallbackProductImage(p.slug, p.categoryId)}
                    alt={p.name}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                  />
                </Link>
                <div className="mt-3 flex items-baseline justify-between gap-3">
                  <div>
                    <Link
                      to="/fragrances/$slug"
                      params={{ slug: p.slug }}
                      className="text-sm font-medium text-black hover:text-[#C6A15B]"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {p.name}
                    </Link>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-black/50">
                      {p.family} · {p.concentration}
                    </p>
                  </div>
                  <p className="text-sm text-black">Starting From {formatPrice(startingPrice(p))}</p>
                </div>
                <button
                  onClick={() => removeFromWishlist(p.id)}
                  className="mt-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black/50 transition hover:text-red-600"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </AccountShell>
  );
}
